import { NavHeader } from '@/components/features/shared/NavHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { installmentsOptions } from '@/constants/installmentOptions'
import { AgreementStatus } from '@/domain/agreements/agreement'
import { Collector, CollectorStatus, getCollectorFullName } from '@/domain/collectors/collector'
import { DeathData, DeathState, formatDeathRelationship, formatDeathState } from '@/domain/deathData/deathData'
import { OfferingStatus, ServiceType } from '@/domain/offerings/offering'
import { useAgreement, useCreateMultiAgreement } from '@/hooks/api/useAgreements'
import { useDeathDataByContract } from '@/hooks/api/useDeathData'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useParcel } from '@/hooks/api/useParcels'
import { useSellers } from '@/hooks/api/useSellers'
import { Paths } from '@/routes/paths'
import { convertToDigitStr } from '@/utils/helpers'
import { useNavigate } from '@tanstack/react-router'
import { CheckCircle2, CreditCard, DollarSign, Loader2, MapPin, User, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { DeathDataCollection } from './DeathDataCollection'

interface AddingAdditionalParcelCmpProps {
  parcelId: string
  contractId: string
}

const EXCLUDED_SERVICE_CODES = ['']
const EXHUMATION_CODE = '14'
const INHUMATION_CODE = '3'
const EXCLUDED_STATES = ['exhumed']

export const AddingAdditionalParcelCmp = ({ parcelId, contractId }: AddingAdditionalParcelCmpProps) => {
  const navigate = useNavigate()
  const { data: parcel, isLoading: isLoadingParcel } = useParcel(parcelId)
  const { data: mainAgreement, isLoading: isLoadingAgreement } = useAgreement(contractId)
  const { data: offerings, isLoading: isLoadingOfferings } = useOfferings({
    status: OfferingStatus.ACTIVE,
    isAdditional: true,
    serviceType: ServiceType.PARCELAS,
    limit: 100
  })
  const { data: deathDataByContract, isLoading: isLoadingDeathData } = useDeathDataByContract(contractId)
  const { data: sellers = [] } = useSellers({ status: CollectorStatus.Active })
  const createMultiMutation = useCreateMultiAgreement()

  const [selectedOfferings, setSelectedOfferings] = useState<string[]>([])
  const [selectedSellerId, setSelectedSellerId] = useState<string | undefined>(undefined)
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'services' | 'deathData' | 'paymentPlan' | 'confirmation'>('services')
  const [deathData, setDeathData] = useState<DeathData[]>([])
  const [selectedExistingDeathData, setSelectedExistingDeathData] = useState<DeathData[]>([])

  const [paymentMethod, setPaymentMethod] = useState<'full' | 'plan'>('full')
  const [showInitialAmount, setShowInitialAmount] = useState(false)
  const [initialAmount, setInitialAmount] = useState<number | undefined>(undefined)
  const [installmentAmount, setInstallmentAmount] = useState<number | undefined>(undefined)
  const [installments, setInstallments] = useState<number | undefined>(undefined)

  const uniqueOfferings = useMemo(() => {
    if (!offerings) return []
    const seen = new Set<string>()
    return offerings.filter(o => {
      if (seen.has(o.code)) return false
      seen.add(o.code)
      return true
    })
  }, [offerings])

  const uniqueSelectedOfferings = useMemo(() => {
    if (!offerings) return []
    const seen = new Set<string>()
    return selectedOfferings.map(id => offerings.find(o => o.id === id)).filter((o): o is typeof offerings[number] => !!o && !seen.has(o.code) && (seen.add(o.code), true))
  }, [offerings, selectedOfferings])

  const needsDeathData = useMemo(() => {
    return uniqueSelectedOfferings.some(o => o.code === INHUMATION_CODE)
  }, [uniqueSelectedOfferings])

  const availableDeathData = useMemo(() => {
    if (!deathDataByContract) return []
    return deathDataByContract.filter(d => !EXCLUDED_STATES.includes(d.state || ''))
  }, [deathDataByContract])

  const hasExhumationService = useMemo(() => {
    return uniqueSelectedOfferings.some(o => o.code === EXHUMATION_CODE)
  }, [uniqueSelectedOfferings])


  if (isLoadingParcel || isLoadingAgreement || isLoadingOfferings || isLoadingDeathData) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!parcel || !mainAgreement || !offerings) {
    return <div>Datos no encontrados</div>
  }

  const handleToggleOffering = (offeringId: string) => {
    setSelectedOfferings(prev =>
      prev.includes(offeringId)
        ? prev.filter(id => id !== offeringId)
        : [...prev, offeringId]
    )
  }

  const handleDeathDataSubmit = (data: DeathData[]) => {
    setDeathData(data)
    setStep('confirmation')
  }

  const handleBackFromDeathData = () => {
    setStep('paymentPlan')
  }

  const handleBackFromPaymentPlan = () => {
    setStep('services')
  }

  const handleNextStep = () => {
    if (!needsDeathData) setDeathData([])
    if (uniqueSelectedOfferings.length > 1) {
      setStep(needsDeathData ? 'deathData' : 'confirmation')
    } else {
      setStep(needsDeathData ? 'deathData' : 'paymentPlan')
    }
  }

  const handleBackFromConfirmation = () => {
    if (uniqueSelectedOfferings.length > 1) {
      setStep('services')
    } else {
      if (needsDeathData) setStep('deathData')
      else setStep('paymentPlan')
    }
  }

  const handleNextFromPaymentPlan = () => {
    if (uniqueSelectedOfferings.length > 1) {
      setStep(needsDeathData ? 'deathData' : 'confirmation')
    } else {
      setStep('confirmation')
    }
  }

  const onSubmit = async () => {
    if (selectedOfferings.length === 0) return

    setIsProcessing(true)
    try {
      const agreementsToCreate = uniqueSelectedOfferings.map(offering => ({
        userId: mainAgreement.userId,
        offeringId: offering.id,
        amount: offering.basePrice || 0,
        startDate: new Date(),
        status: AgreementStatus.Pending,
        mainContractId: mainAgreement.id,
        sellerId: selectedSellerId,
      }))

      const response = await createMultiMutation.mutateAsync({
        agreements: agreementsToCreate,
        parcel: {
          parcelId: parcelId
        },
        deathData: deathData.length > 0 ? deathData : undefined,
        updateDeathData: selectedExistingDeathData.length > 0 ? selectedExistingDeathData.map(d => ({ ...d, disintermentDate: new Date(), state: DeathState.EXHUMED })) : undefined,
        paymentPlan: paymentMethod === 'plan' ? {
          initialAmount: initialAmount || 0,
          installmentAmount: installmentAmount || 0,
          installments: installments || 1,
        } : undefined
      })

      if (response.billingPeriodId && response.payments.length > 0) {
        navigate({ to: `${Paths.Management.SubscriptionPeriodPayments}/${response.billingPeriodId}/${Paths.Management.Payments}/${response.payments[0].id}` })
      } else {
        if (response.payments.length > agreementsToCreate.length) {
          navigate({ to: `${Paths.Management.MultiPayments}/${response.payments[response.payments.length - 1].id}` })
        }
        else {
          navigate({ to: `${Paths.Management.Contracts}/${mainAgreement.id}/${Paths.Management.Payments}/${response.payments[0].id}` })
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsProcessing(false)
    }
  }

  const totalAmount = uniqueSelectedOfferings.reduce((sum, off) => sum + (off.basePrice || 0), 0)
  const displayedTotalAmount = paymentMethod === 'plan' ? (installmentAmount ?? 0) + (initialAmount ?? 0) : totalAmount

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <NavHeader>
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Agregar Adicionales</h1>
          <p className="text-muted-foreground">
            {step === 'services' && (
              <>Seleccione los servicios adicionales para la parcela con contrato <span className="font-bold text-foreground">{mainAgreement.contractNumber}</span></>
            )}
            {step === 'paymentPlan' && (
              <>Configure el método de pago para los servicios adicionales de la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
            )}
            {step === 'deathData' && (
              <>Ingrese los datos de los fallecidos para la ocasión en la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
            )}
            {step === 'confirmation' && (
              <>Confirme la operación para el proceso de adicionales de la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
            )}
          </p>
        </div>
      </NavHeader>

      {/* Step: Services Selection */}
      {step === 'services' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2 space-y-4">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Servicios Disponibles
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {uniqueOfferings.filter(o => !EXCLUDED_SERVICE_CODES.includes(o.code)).map((offering) => {
                  const isSelected = selectedOfferings.includes(offering.id)
                  return (
                    <div
                      key={offering.id}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200 cursor-pointer'
                        }`}
                      onClick={() => handleToggleOffering(offering.id)}
                    >
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleOffering(offering.id)}
                        />
                        <div className='flex items-center gap-1'>
                          <p className="font-bold">
                            <span className='font-mono'>
                              {offering.code}</span> - <span>{offering.name}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">{offering.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold">${convertToDigitStr(offering.basePrice)}</p>
                      </div>
                    </div>
                  )
                })}
                {hasExhumationService && availableDeathData.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Seleccionar fallecidos existentes</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {availableDeathData.map((death) => (
                        <div
                          key={death.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${selectedExistingDeathData.some(d => d.id === death.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                          onClick={() => {
                            setSelectedExistingDeathData(prev =>
                              prev.some(d => d.id === death.id)
                                ? prev.filter(d => d.id !== death.id)
                                : [...prev, death]
                            )
                          }}
                        >
                          <Checkbox
                            checked={selectedExistingDeathData.some(d => d.id === death.id)}
                            onCheckedChange={() => {
                              setSelectedExistingDeathData(prev =>
                                prev.some(d => d.id === death.id)
                                  ? prev.filter(d => d.id !== death.id)
                                  : [...prev, death]
                              )
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{death.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {death.docType} {death.docNumber}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{formatDeathState(death.state || DeathState.DEATH)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-3 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground">Vendedor</p>
                  <Select
                    onValueChange={(value: string) => {
                      setSelectedSellerId(value === '__none__' ? undefined : value)
                    }}
                    value={selectedSellerId ?? '__none__'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar vendedor (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin vendedor asignado</SelectItem>
                      {sellers.map((seller) => (
                        <SelectItem key={seller.id} value={seller.id}>
                          {seller.name} ({seller.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-blue-100 bg-blue-50/30">
                <CardHeader className="pb-3 border-b border-blue-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Sección</p>
                      <p className="font-medium text-lg">{parcel.section}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Fila</p>
                      <p className="font-medium text-lg">{parcel.fila}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Parcela</p>
                      <p className="font-medium text-lg">#{parcel.parcela}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-lg">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-lg">Resumen de Cargos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {uniqueSelectedOfferings.map(off => (
                      <div key={off.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground line-clamp-1 mr-2">{off.name}</span>
                        <span className="font-mono">${convertToDigitStr(off.basePrice || 0)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-4">
                    <span className="font-bold">Total a Cobrar</span>
                    <span className="text-xl font-bold text-blue-700 font-mono">${convertToDigitStr(displayedTotalAmount)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleNextStep}
              disabled={isProcessing || selectedOfferings.length === 0}
              className="flex-1"
            >
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Siguiente'}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Payment Plan */}
      {step === 'paymentPlan' && (
        <div className="space-y-6">
          <div className="space-y-6">
            <Card className="border-gray-200">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                  <CardTitle className="text-lg">Método de Pago</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="radiogroup" aria-label="Método de pago">
                  <div
                    role="radio"
                    aria-checked={paymentMethod === 'full'}
                    tabIndex={0}
                    onClick={() => setPaymentMethod('full')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setPaymentMethod('full')
                      }
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${paymentMethod === 'full' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`p-2 rounded-full ${paymentMethod === 'full' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <Wallet className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">Pago Total</p>
                      <p className="text-xs text-muted-foreground">Abonar el 100% al contado</p>
                    </div>
                  </div>
                  <div
                    role="radio"
                    aria-checked={paymentMethod === 'plan'}
                    tabIndex={0}
                    onClick={() => setPaymentMethod('plan')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setPaymentMethod('plan')
                      }
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${paymentMethod === 'plan' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`p-2 rounded-full ${paymentMethod === 'plan' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">Plan de Pago</p>
                      <p className="text-xs text-muted-foreground">Financiar en cuotas</p>
                    </div>
                  </div>
                </div>

                {paymentMethod === 'plan' && (
                  <div className="space-y-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Switch
                        id="has-initial-extra"
                        checked={showInitialAmount}
                        onCheckedChange={(checked) => {
                          setShowInitialAmount(checked)
                          if (!checked) setInitialAmount(0)
                        }}
                      />
                      <label htmlFor="has-initial-extra" className="text-sm font-medium cursor-pointer">
                        ¿Posee un monto de entrega inicial?
                      </label>
                    </div>

                    {showInitialAmount && (
                      <div className="space-y-2 max-w-xs animate-in fade-in zoom-in duration-200">
                        <label className="text-sm font-medium">Monto de Entrega Inicial</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            type="number"
                            step="1"
                            placeholder="0.00"
                            className="pl-10 text-lg font-bold"
                            value={initialAmount ?? ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0
                              setInitialAmount(value)
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Monto a financiar</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            type="number"
                            step="1"
                            placeholder="0.00"
                            className="pl-10 text-lg font-bold"
                            value={installmentAmount ?? ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0
                              setInstallmentAmount(value)
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Cuotas</label>
                        <Select
                          onValueChange={(value) => setInstallments(Number(value))}
                          value={installments?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Cuotas" />
                          </SelectTrigger>
                          <SelectContent>
                            {installmentsOptions.map((n) => (
                              <SelectItem key={n} value={n.toString()}>
                                {n} cuotas
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {installments && installmentAmount && (installmentAmount / installments) > 0 && (
                      <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                        <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                          <Wallet className="h-4 w-4" />
                          <span>
                            Se financiará el monto de <strong>${convertToDigitStr(installmentAmount.toString())}</strong> en <strong>{installments} cuotas</strong> de <strong>${convertToDigitStr((installmentAmount / installments).toString())}</strong>.
                            {showInitialAmount && (initialAmount || 0) > 0 && <>
                              <br />
                              Además, se hará una entrega inicial de <strong>${convertToDigitStr((initialAmount || 0).toString())}</strong>
                            </>}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {paymentMethod === 'full' && (totalAmount || 0) > 0 && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                    <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                      <Wallet className="h-4 w-4" />
                      <span>Se generará un cobro total por <strong>${convertToDigitStr(totalAmount?.toString() || '0')}</strong> abonado en un solo pago.</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-lg">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg">Resumen de Cargos</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {uniqueSelectedOfferings.map(off => (
                    <div key={off.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground line-clamp-1 mr-2">{off.name}</span>
                      <span className="font-mono">${convertToDigitStr(off.basePrice || 0)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-4">
                  <span className="font-bold">Total a Cobrar</span>
                  <span className="text-xl font-bold text-blue-700 font-mono">${convertToDigitStr(displayedTotalAmount)}</span>
                </div>
                {paymentMethod === 'plan' && installmentAmount && installments && (
                  <div className="pt-4 border-t border-gray-100 space-y-2">
                    {showInitialAmount && initialAmount && initialAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Entrega inicial</span>
                        <span className="font-mono">${convertToDigitStr(initialAmount.toString())}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Monto a financiar</span>
                      <span className="font-mono">${convertToDigitStr(installmentAmount.toString())}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>{installments} cuotas de</span>
                      <span className="font-mono text-blue-700 font-bold">${convertToDigitStr((installmentAmount / installments).toString())}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleBackFromPaymentPlan}
              disabled={isProcessing}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              variant="info"
              onClick={handleNextFromPaymentPlan}
              disabled={isProcessing || selectedOfferings.length === 0}
              className="flex-1"
            >
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Siguiente'}
            </Button>
          </div>
        </div>
      )}

      {step === 'deathData' && (
        <DeathDataCollection
          offering={{ id: '', code: '', name: '' }}
          onSubmit={handleDeathDataSubmit}
          onBack={handleBackFromDeathData}
          peopleList={deathData}
          onPeopleListChange={setDeathData}
        />
      )}

      {step === 'confirmation' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2 space-y-4">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Confirmar y Generar Cargos
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {uniqueSelectedOfferings.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-bold mb-1">Servicios Seleccionados</p>
                      <div className="space-y-2">
                        {uniqueSelectedOfferings.map(off => (
                          <div key={off.id} className="flex justify-between text-sm">
                            <span className="line-clamp-1 mr-2">{off.name}</span>
                            <span className="font-mono">${convertToDigitStr(off.basePrice || 0)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {paymentMethod === 'plan' && installmentAmount && installments && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex gap-3">
                    <CreditCard className="h-5 w-5 text-purple-600 shrink-0" />
                    <div className="text-sm text-purple-800">
                      <p className="font-bold mb-1">Plan de Pago</p>
                      <div className="space-y-1">
                        {showInitialAmount && initialAmount && initialAmount > 0 && (
                          <p>Monto de entrega inicial: <strong>${convertToDigitStr(initialAmount.toString())}</strong></p>
                        )}
                        <p>Monto a financiar: <strong>${convertToDigitStr(installmentAmount.toString())}</strong></p>
                        <p>Cuotas: <strong>{installments}</strong></p>
                        <p>Valor de cada cuota: <strong>${convertToDigitStr((installmentAmount / installments).toString())}</strong></p>
                      </div>
                    </div>
                  </div>
                )}
                {paymentMethod === 'full' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                    <Wallet className="h-5 w-5 text-green-600 shrink-0" />
                    <div className="text-sm text-green-800">
                      <p className="font-bold mb-1">Método de Pago</p>
                      <p>Pago total de <strong>${convertToDigitStr(totalAmount.toString())}</strong> en un solo pago.</p>
                    </div>
                  </div>
                )}
                {(() => {
                  const seller = sellers.find((s: Collector) => s.id === selectedSellerId)
                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex gap-3">
                      <User className="h-5 w-5 text-gray-600 shrink-0" />
                      <div className="text-sm text-gray-800">
                        <p className="font-bold mb-1">Vendedor</p>
                        <p>{getCollectorFullName(seller)}</p>
                      </div>
                    </div>
                  )
                })()}
                {selectedExistingDeathData.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0" />
                    <div className="text-sm text-amber-800 space-y-3">
                      <p className="font-bold mb-1">Fallecidos Seleccionados (Exhumación)</p>
                      <div className="space-y-2">
                        {selectedExistingDeathData.map((person, index) => (
                          <div key={person.id || index} className="border border-amber-200 rounded-lg p-3 bg-white/50">
                            <div className="flex justify-between items-start">
                              <p className="font-bold text-amber-900">{index + 1}. {person.name}</p>
                              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">{formatDeathState(person.state || DeathState.DEATH)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                              {person.docType && (
                                <div>
                                  <span className="font-medium text-amber-700">Tipo Doc:</span>
                                  <span className="ml-1">{person.docType}</span>
                                </div>
                              )}
                              {person.docNumber && (
                                <div>
                                  <span className="font-medium text-amber-700">Nro Doc:</span>
                                  <span className="ml-1">{person.docNumber}</span>
                                </div>
                              )}
                              {person.birthDate && (
                                <div>
                                  <span className="font-medium text-amber-700">Fecha Nac.:</span>
                                  <span className="ml-1">{new Date(person.birthDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.deathDate && (
                                <div>
                                  <span className="font-medium text-amber-700">Fecha Fallecimiento:</span>
                                  <span className="ml-1">{new Date(person.deathDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.relationshipWithDeceased && (
                                <div>
                                  <span className="font-medium text-amber-700">Parentesco:</span>
                                  <span className="ml-1">{formatDeathRelationship(person.relationshipWithDeceased)}</span>
                                </div>
                              )}
                              {person.mortuary && (
                                <div>
                                  <span className="font-medium text-amber-700">Funeraria:</span>
                                  <span className="ml-1">{person.mortuary}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {deathData?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    <div className="text-sm text-green-800 space-y-3">
                      <p className="font-bold mb-1">Datos del Fallecido</p>
                      <div className="space-y-2">
                        {deathData.map((person, index) => {
                          const personLabel = `${index + 1}. ${person.name}`
                          return (
                            <div key={person.id || index} className="border border-green-200 rounded-lg p-3 bg-white/50">
                              <p className="font-bold text-green-900">{personLabel}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                                {person.docType && (
                                  <div>
                                    <span className="font-medium text-green-700">Tipo Doc:</span>
                                    <span className="ml-1">{person.docType}</span>
                                  </div>
                                )}
                                {person.docNumber && (
                                  <div>
                                    <span className="font-medium text-green-700">Nro Doc:</span>
                                    <span className="ml-1">{person.docNumber}</span>
                                  </div>
                                )}
                                {person.birthDate && (
                                  <div>
                                    <span className="font-medium text-green-700">Fecha Nac.:</span>
                                    <span className="ml-1">{new Date(person.birthDate).toLocaleDateString('es-AR')}</span>
                                  </div>
                                )}
                                {person.deathDate && (
                                  <div>
                                    <span className="font-medium text-green-700">Fecha Fallecimiento:</span>
                                    <span className="ml-1">{new Date(person.deathDate).toLocaleDateString('es-AR')}</span>
                                  </div>
                                )}
                                {person.state && (
                                  <div>
                                    <span className="font-medium text-green-700">Estado:</span>
                                    <span className="ml-1">{formatDeathState(person.state)}</span>
                                  </div>
                                )}
                                {person.cremationDate && (
                                  <div>
                                    <span className="font-medium text-green-700">Fecha Cremación:</span>
                                    <span className="ml-1">{new Date(person.cremationDate).toLocaleDateString('es-AR')}</span>
                                  </div>
                                )}
                                {person.intermentDate && (
                                  <div>
                                    <span className="font-medium text-green-700">Fecha inhumación:</span>
                                    <span className="ml-1">{new Date(person.intermentDate).toLocaleDateString('es-AR')}</span>
                                  </div>
                                )}
                                {person.reductionDate && (
                                  <div>
                                    <span className="font-medium text-green-700">Fecha Reducción:</span>
                                    <span className="ml-1">{new Date(person.reductionDate).toLocaleDateString('es-AR')}</span>
                                  </div>
                                )}
                                {person.thumbLevel && (
                                  <div>
                                    <span className="font-medium text-green-700">Nivel en parcela:</span>
                                    <span className="ml-1">{person.thumbLevel}</span>
                                  </div>
                                )}
                                {person.relationshipWithDeceased && (
                                  <div>
                                    <span className="font-medium text-green-700">Parentesco:</span>
                                    <span className="ml-1">{formatDeathRelationship(person.relationshipWithDeceased)}</span>
                                  </div>
                                )}
                                <div className="col-span-2 mt-1"></div>
                                {person.entryNumber && (
                                  <div>
                                    <span className="font-medium text-green-700">Nro Acta:</span>
                                    <span className="ml-1">{person.entryNumber}</span>
                                  </div>
                                )}
                                {person.volume && (
                                  <div>
                                    <span className="font-medium text-green-700">Tomo:</span>
                                    <span className="ml-1">{person.volume}</span>
                                  </div>
                                )}
                                {person.year && (
                                  <div>
                                    <span className="font-medium text-green-700">Año:</span>
                                    <span className="ml-1">{person.year}</span>
                                  </div>
                                )}
                                {person.leaf && (
                                  <div>
                                    <span className="font-medium text-green-700">Folio:</span>
                                    <span className="ml-1">{person.leaf}</span>
                                  </div>
                                )}
                                {person.office && (
                                  <div>
                                    <span className="font-medium text-green-700">Oficina:</span>
                                    <span className="ml-1">{person.office}</span>
                                  </div>
                                )}
                                {person.mortuary && (
                                  <div>
                                    <span className="font-medium text-green-700">Funeraria:</span>
                                    <span className="ml-1">{person.mortuary}</span>
                                  </div>
                                )}
                                {person.medicOfDeceased && (
                                  <div>
                                    <span className="font-medium text-green-700">Médico:</span>
                                    <span className="ml-1">{person.medicOfDeceased}</span>
                                  </div>
                                )}
                                {person.deathCertificateAuthorization && (
                                  <div>
                                    <span className="font-medium text-green-700">Autorización de sepultura:</span>
                                    <span className="ml-1">{person.deathCertificateAuthorization}</span>
                                  </div>
                                )}
                                {person.notes && (
                                  <div className="col-span-2">
                                    <span className="font-medium text-green-700">Notas:</span>
                                    <span className="ml-1 block whitespace-break-spaces">{person.notes}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-blue-100 bg-blue-50/30">
                <CardHeader className="pb-3 border-b border-blue-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Sección</p>
                      <p className="font-medium text-lg">{parcel.section}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Fila</p>
                      <p className="font-medium text-lg">{parcel.fila}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Parcela</p>
                      <p className="font-medium text-lg">#{parcel.parcela}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-lg">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-lg">Resumen de Cargos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {uniqueSelectedOfferings.map(off => (
                      <div key={off.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground line-clamp-1 mr-2">{off.name}</span>
                        <span className="font-mono">${convertToDigitStr(off.basePrice || 0)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-4">
                    <span className="font-bold">Total a Cobrar</span>
                    <span className="text-xl font-bold text-blue-700 font-mono">${convertToDigitStr(displayedTotalAmount)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleBackFromConfirmation}
              disabled={isProcessing}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              variant="info"
              onClick={onSubmit}
              disabled={isProcessing || selectedOfferings.length === 0}
              className="flex-1"
            >
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
